import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAbortError, starchartApiService } from '../services/starchartApi';
import { starchartRankHistoryApiService } from '../services/starchartRankHistoryApi';
import type { ChartPoint } from '../utils/chartMath';
import type {
  PopularityWindow,
  StarchartMetric,
  StarchartServedResolution,
} from '../types/starchart';

/** The network total is a concept, not a server, so it needs an id no FQDN can collide with. */
export const NETWORK_OVERLAY_ID = '__network__';

export interface OverlaySeries {
  id: string;
  label: string;
  points: ChartPoint[];
}

export interface UseStarchartOverlaysResult {
  series: OverlaySeries[];
  loading: boolean;
  /** Ids that were requested but returned nothing — shown as a note, never as a page error. */
  empty: string[];
}

/**
 * Load the comparison series for a StarChart chart.
 *
 * Kept apart from `useStarchartServer` so neither hook has two jobs: this one only answers "what
 * else is being drawn". Every metric is supported, but the cost differs — player counts batch into
 * a single request for any number of servers, while tick timings have no batch endpoint upstream
 * and cost one request each, which is why the picker caps the selection.
 */
export function useStarchartOverlays(
  overlayIds: string[],
  networkOverlay: boolean,
  metric: StarchartMetric,
  popularityWindow: PopularityWindow,
  start: Date,
  end: Date,
  resolution: StarchartServedResolution,
): UseStarchartOverlaysResult {
  const [series, setSeries] = useState<OverlaySeries[]>([]);
  const [empty, setEmpty] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // A stable key so the effect does not re-run on a new array with the same contents.
  const overlayKey = overlayIds.join(',');

  const load = useCallback(async () => {
    abortRef.current?.abort();

    const ids = overlayKey ? overlayKey.split(',').filter(Boolean) : [];
    if (ids.length === 0 && !networkOverlay) {
      setSeries([]);
      setEmpty([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;
    setLoading(true);

    try {
      const collected: OverlaySeries[] = [];
      const missing: string[] = [];

      // ── Servers ──────────────────────────────────────────────────────────
      if (ids.length > 0) {
        if (metric === 'players') {
          // The one endpoint that batches: N servers, one round trip.
          const aggregate = await starchartApiService.getAggregate(
            ids,
            start,
            end,
            resolution,
            { signal },
          );
          if (signal.aborted) return;

          for (const id of ids) {
            const entry = aggregate.find((a) => a.server_id === id);
            const points = (entry?.data ?? []).map((p) => ({
              t: p.timestamp * 1000,
              v: p.player_count,
            }));
            if (points.length === 0) missing.push(id);
            else collected.push({ id, label: id, points });
          }
        } else if (metric === 'tps' || metric === 'mspt') {
          // No batch equivalent exists upstream, so these are fetched in parallel and settled
          // independently — one server without tick data must not lose the others.
          const results = await Promise.allSettled(
            ids.map((id) =>
              starchartApiService
                .getServerMspt(id, start, end, resolution, { signal })
                .then((response) => ({ id, response })),
            ),
          );
          if (signal.aborted) return;

          for (const result of results) {
            if (result.status !== 'fulfilled') continue;
            const { id, response } = result.value;
            const points = response.data.map((p) => ({
              t: p.timestamp * 1000,
              // Nulls stay null so a gap renders as a gap rather than a drop to zero.
              v: metric === 'tps' ? p.tps : p.mspt,
            }));
            if (points.every((p) => p.v === null)) missing.push(id);
            else collected.push({ id, label: id, points });
          }
          for (const id of ids) {
            if (!collected.some((s) => s.id === id) && !missing.includes(id)) missing.push(id);
          }
        } else {
          // Popularity history is ARGUS's own, and only exists from when collection started.
          const results = await Promise.allSettled(
            ids.map((id) =>
              starchartRankHistoryApiService
                .getRankHistory(id, popularityWindow, start, end, signal)
                .then((response) => ({ id, response })),
            ),
          );
          if (signal.aborted) return;

          for (const id of ids) {
            const result = results.find(
              (r) => r.status === 'fulfilled' && r.value.id === id,
            ) as PromiseFulfilledResult<{ id: string; response: { data: { captured_at: number; popularity_index: number | null }[] } }> | undefined;
            const points = (result?.value.response.data ?? []).map((p) => ({
              t: p.captured_at * 1000,
              v: p.popularity_index,
            }));
            if (points.length === 0) missing.push(id);
            else collected.push({ id, label: id, points });
          }
        }
      }

      // ── Network total ────────────────────────────────────────────────────
      if (networkOverlay) {
        try {
          let points: ChartPoint[] = [];

          if (metric === 'players') {
            const response = await starchartApiService.getNetworkTimeseries(
              start,
              end,
              resolution,
              { signal },
            );
            points = response.data.map((p) => ({ t: p.timestamp * 1000, v: p.player_count }));
          } else if (metric === 'tps' || metric === 'mspt') {
            const response = await starchartApiService.getNetworkMspt(start, end, resolution, {
              signal,
            });
            points = response.data.map((p) => ({
              t: p.timestamp * 1000,
              v: metric === 'tps' ? p.tps : p.mspt,
            }));
          } else {
            const response = await starchartApiService.getNetworkPopularity(
              popularityWindow,
              start,
              end,
              { signal },
            );
            points = response.data.map((p) => ({
              t: p.timestamp * 1000,
              v: p.popularity_index,
            }));
          }

          if (signal.aborted) return;

          if (points.length === 0) missing.push(NETWORK_OVERLAY_ID);
          else {
            collected.push({
              id: NETWORK_OVERLAY_ID,
              label: 'Network total',
              points,
            });
          }
        } catch (err) {
          if (isAbortError(err) || signal.aborted) return;
          missing.push(NETWORK_OVERLAY_ID);
        }
      }

      setSeries(collected);
      setEmpty(missing);
      setLoading(false);
    } catch (err) {
      // A cancelled load is not a failure; painting an error over good data is the classic bug.
      if (isAbortError(err) || abortRef.current?.signal.aborted) return;
      setSeries([]);
      setEmpty([...(overlayKey ? overlayKey.split(',') : []), ...(networkOverlay ? [NETWORK_OVERLAY_ID] : [])]);
      setLoading(false);
    }
  }, [overlayKey, networkOverlay, metric, popularityWindow, start, end, resolution]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  return useMemo(() => ({ series, loading, empty }), [series, loading, empty]);
}
